<template>
    <!-- Onglet Prévisions (remplace « Prévisions CA » et « Prévision journalière ») -->
    <div v-if="!ca || !journaliere" class="pp-vide">
        <i class="ri-loader-4-line" :class="{ 'pp-spin': chargement }"></i>
        {{ chargement ? 'Chargement des prévisions…' : 'Aucune donnée.' }}
    </div>
    <div v-else>
        <div class="pp-g2">
            <!-- Combien allez-vous vendre ? -->
            <div class="pp-bloc">
                <div class="pp-bloc__tete">
                    <div class="pp-bloc__titre"><i class="ri-focus-2-line"></i>Combien allez-vous vendre ?</div>
                    <div class="pp-seg" role="group" aria-label="Horizon">
                        <button v-for="h in horizons" :key="h.id" type="button" :class="{ 'pp-seg--on': horizon === h.id }" @click="horizon = h.id">{{ h.libelle }}</button>
                    </div>
                </div>
                <div class="pp-bloc__corps">
                    <div class="pp-g3">
                        <div class="pp-scen">
                            <div class="pp-scen__lbl"><i class="ri-arrow-down-line pp-txt-d"></i>Mauvaise période</div>
                            <div class="pp-scen__val">{{ montant(vue.bas) }}</div>
                        </div>
                        <div class="pp-scen pp-scen--cible">
                            <div class="pp-scen__lbl"><i class="ri-focus-2-line"></i>Le plus probable</div>
                            <div class="pp-scen__val">{{ montant(vue.cible) }}</div>
                        </div>
                        <div class="pp-scen">
                            <div class="pp-scen__lbl"><i class="ri-arrow-up-line pp-txt-s"></i>Bonne période</div>
                            <div class="pp-scen__val">{{ montant(vue.haut) }}</div>
                        </div>
                    </div>
                    <div class="pp-cascade">
                        <div class="pp-casc"><div class="pp-casc__lbl">Chiffre d'affaires</div><div class="pp-casc__val">{{ montant(vue.cible) }}</div></div>
                        <div class="pp-casc"><div class="pp-casc__lbl">− Coût des achats</div><div class="pp-casc__val">{{ montant(vue.achats) }}</div></div>
                        <div class="pp-casc"><div class="pp-casc__lbl">− Charges</div><div class="pp-casc__val pp-txt-w">{{ montant(vue.charges) }}</div></div>
                        <div class="pp-casc" :class="vue.net === null ? '' : (vue.net >= 0 ? 'pp-casc--net' : 'pp-casc--neg')">
                            <div class="pp-casc__lbl">= Bénéfice net</div>
                            <div class="pp-casc__val" :class="vue.net === null ? 'pp-txt-m' : (vue.net >= 0 ? 'pp-txt-s' : 'pp-txt-d')">{{ montant(vue.net) }}</div>
                        </div>
                    </div>
                    <p class="pp-legende"><i class="ri-information-line"></i><span>{{ vue.legende }}</span></p>
                </div>
            </div>

            <!-- Jours forts -->
            <div class="pp-bloc">
                <div class="pp-bloc__tete">
                    <div class="pp-bloc__titre"><i class="ri-bar-chart-2-line"></i>Vos jours forts <small>moyenne des {{ journaliere.fenetre_jours }} derniers jours</small></div>
                </div>
                <div class="pp-bloc__corps">
                    <apexchart type="bar" height="250" :options="optionsProfil" :series="seriesProfil"></apexchart>
                </div>
            </div>
        </div>

        <!-- Les 7 prochains jours -->
        <div class="pp-bloc pp-mt">
            <div class="pp-bloc__tete">
                <div class="pp-bloc__titre"><i class="ri-calendar-event-line"></i>Les 7 prochains jours</div>
                <span class="pp-badge pp-b-p">Total {{ montant(journaliere.total_ca_semaine) }}</span>
            </div>
            <div class="pp-bloc__corps">
                <div class="pp-semaine">
                    <div v-for="j in journaliere.prevision_7j" :key="j.date" class="pp-jour"
                         :class="{ 'pp-jour--fort': estFort(j), 'pp-jour--ferme': j.taux_ouverture < 0.8 }">
                        <div class="pp-jour__n">{{ j.libelle.slice(0, 3) }}</div>
                        <div class="pp-jour__d">{{ dateCourte(j.date) }}</div>
                        <div class="pp-jour__b"><span :style="{ height: hauteurBarre(j) + 'px' }"></span></div>
                        <div class="pp-jour__v">{{ montantCourt(j.ca_prevu) }}</div>
                        <div class="pp-jour__tag">
                            <span v-if="j.base_historique === 0">jamais ouvert</span>
                            <span v-else-if="estFort(j)" class="pp-badge pp-b-s">jour fort</span>
                            <span v-else-if="j.taux_ouverture < 0.8">ouvert {{ Math.round(j.taux_ouverture * 100) }} % du temps</span>
                            <span v-else>&nbsp;</span>
                        </div>
                    </div>
                </div>
                <p class="pp-legende"><i class="ri-information-line"></i>
                    <span>Pour chaque jour, la moyenne de ce jour de la semaine sur les {{ journaliere.fenetre_jours }} derniers jours, jours de fermeture compris.</span></p>
            </div>
        </div>
    </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import { montant, montantCourt, MOIS_COURTS } from "./appelpp";

export default {
    name: "TabPrevisions",
    components: { apexchart: VueApexCharts },
    props: {
        ca: { type: Object, default: null },
        journaliere: { type: Object, default: null },
        chargement: { type: Boolean, default: false }
    },
    data() {
        return {
            horizons: [{ id: "jour", libelle: "Un jour" }, { id: "semaine", libelle: "7 jours" }, { id: "mois", libelle: "30 jours" }],
            horizon: "mois"
        };
    },
    computed: {
        r() { return this.ca.rentabilite; },
        // Taux appliqué à un CA TTC (marge HT / CA TTC), tel que calculé par le serveur ; null si inconnu.
        tauxApplique() {
            const c = this.r.cible;
            return c.marge_brute === null || !(c.ca > 0) ? null : c.marge_brute / c.ca;
        },
        vue() {
            const s = this.ca.scenarios, p = this.ca.projection_mois;
            let cible, ec, charges, legende;
            if (this.horizon === "jour") {
                cible = s.cible; ec = s.ecart_type; charges = this.r.charges_jour;
                legende = `Un jour d'ouverture moyen sur les ${this.ca.fenetre_jours} derniers jours (${this.ca.jours_actifs} jours d'activité, aujourd'hui exclu). Les charges du mois sont réparties sur les jours d'ouverture.`;
            } else if (this.horizon === "semaine") {
                cible = this.journaliere.total_ca_semaine;
                const joursOuverts = 7 * this.ca.jours_actifs / this.ca.fenetre_jours;
                ec = s.ecart_type * Math.sqrt(joursOuverts);
                charges = this.r.charges_mensuel_total * 7 / 30;
                legende = "Somme des 7 prochains jours selon vos habitudes de chaque jour de la semaine. Charges : 7/30 du mois.";
            } else {
                cible = p.ca; ec = p.ca_haut - p.ca; charges = p.charges;
                legende = `Au rythme des ${this.ca.fenetre_jours} derniers jours, aujourd'hui exclu. Les charges du mois sont déduites en entier.`;
            }
            const marge = this.tauxApplique === null ? null : cible * this.tauxApplique;
            return {
                cible, bas: Math.max(0, cible - ec), haut: cible + ec, charges, legende,
                achats: marge === null ? null : cible - marge,
                net: marge === null ? null : marge - charges
            };
        },
        maxJour() { return Math.max(1, ...this.journaliere.profil_semaine.map((j) => j.moyenne_ca)); },
        seriesProfil() { return [{ name: "CA moyen", data: this.journaliere.profil_semaine.map((j) => Math.round(j.moyenne_ca)) }]; },
        optionsProfil() {
            const max = this.maxJour;
            return {
                chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
                xaxis: { categories: this.journaliere.profil_semaine.map((j) => j.libelle.slice(0, 3)) },
                yaxis: { labels: { formatter: (v) => montantCourt(v) } },
                colors: [({ value }) => (value >= max * 0.95 ? "#26BF94" : "#845ADF")],
                plotOptions: { bar: { borderRadius: 5, columnWidth: "55%" } },
                dataLabels: { enabled: false },
                grid: { borderColor: "#eef1f6" },
                tooltip: { y: { formatter: (v) => montant(v) } }
            };
        }
    },
    methods: {
        montant, montantCourt,
        estFort(j) { return j.ca_prevu > 0 && j.ca_prevu >= this.maxJour * 0.95; },
        hauteurBarre(j) { return Math.max(4, (j.ca_prevu / this.maxJour) * 42); },
        dateCourte(iso) {
            const [, m, d] = iso.split("-");
            return parseInt(d, 10) + " " + MOIS_COURTS[parseInt(m, 10) - 1];
        }
    }
};
</script>

<style scoped>
.pp-vide { text-align: center; color: #94a3b8; padding: 2.5rem 1rem; }
.pp-spin { display: inline-block; animation: pp-tourne .8s linear infinite; }
@keyframes pp-tourne { to { transform: rotate(360deg); } }
.pp-g2 { display: grid; grid-template-columns: 3fr 2fr; gap: 1rem; }
.pp-g3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .75rem; }
.pp-mt { margin-top: 1rem; }
.pp-bloc { border: 1px solid #e2e8f0; border-radius: .625rem; background: #fff; }
.pp-bloc__tete { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; justify-content: space-between; padding: .8rem 1rem; border-bottom: 1px solid #e2e8f0; }
.pp-bloc__titre { font-weight: 700; color: #0f172a; font-size: .88rem; display: flex; align-items: center; gap: .4rem; }
.pp-bloc__titre i { color: rgb(var(--primary)); }
.pp-bloc__titre small { font-weight: 500; color: #94a3b8; font-size: .72rem; }
.pp-bloc__corps { padding: 1rem; }
.pp-seg { display: inline-flex; background: #f1f5f9; border-radius: .5rem; padding: 3px; }
.pp-seg button { border: 0; background: none; padding: .35rem .8rem; font-size: .76rem; font-weight: 600; color: #64748b; border-radius: .375rem; cursor: pointer; }
.pp-seg--on { background: #fff !important; color: rgb(var(--primary)) !important; box-shadow: 0 1px 3px rgb(15 23 42 / .08); }
.pp-scen { padding: .9rem; border-radius: .625rem; border: 1px solid #e2e8f0; background: #f8fafc; }
.pp-scen__lbl { font-size: .72rem; color: #64748b; font-weight: 600; display: flex; align-items: center; gap: .3rem; }
.pp-scen__val { font-size: 1.15rem; font-weight: 800; color: #0f172a; margin-top: .25rem; font-variant-numeric: tabular-nums; }
.pp-scen--cible { background: rgb(var(--primary) / .06); border-color: rgb(var(--primary) / .3); }
.pp-scen--cible .pp-scen__val, .pp-scen--cible i { color: rgb(var(--primary)); }
.pp-cascade { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .6rem; margin-top: 1rem; }
.pp-casc { padding: .7rem .8rem; border-radius: .5rem; background: #f8fafc; }
.pp-casc__lbl { font-size: .66rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; }
.pp-casc__val { font-weight: 800; font-size: 1rem; margin-top: .2rem; font-variant-numeric: tabular-nums; color: #0f172a; }
.pp-casc--net { border: 2px solid rgb(var(--success) / .35); background: rgb(var(--success) / .06); }
.pp-casc--neg { border: 2px solid rgb(var(--danger) / .35); background: rgb(var(--danger) / .06); }
.pp-legende { font-size: .74rem; color: #64748b; display: flex; gap: .4rem; align-items: flex-start; margin: .75rem 0 0; }
.pp-semaine { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: .5rem; }
.pp-jour { border: 1px solid #e2e8f0; border-radius: .5rem; padding: .6rem .4rem; text-align: center; background: #fff; }
.pp-jour__n { font-size: .7rem; font-weight: 700; color: #64748b; text-transform: uppercase; }
.pp-jour__d { font-size: .68rem; color: #94a3b8; }
.pp-jour__v { font-weight: 800; font-size: .86rem; margin-top: .35rem; color: #0f172a; font-variant-numeric: tabular-nums; }
.pp-jour__b { height: 42px; display: flex; align-items: flex-end; justify-content: center; margin-top: .4rem; }
.pp-jour__b span { width: 60%; border-radius: 4px 4px 0 0; background: rgb(var(--primary) / .75); }
.pp-jour--fort .pp-jour__b span { background: rgb(var(--success)); }
.pp-jour--ferme { background: #f8fafc; }
.pp-jour__tag { font-size: .62rem; margin-top: .3rem; color: #94a3b8; min-height: 1.1rem; }
.pp-badge { display: inline-flex; align-items: center; gap: .25rem; font-size: .68rem; font-weight: 700; padding: .15rem .5rem; border-radius: 99px; }
.pp-b-s { background: rgb(var(--success) / .12); color: rgb(var(--success)); }
.pp-b-p { background: rgb(var(--primary) / .1); color: rgb(var(--primary)); }
.pp-txt-s { color: rgb(var(--success)) !important; }
.pp-txt-d { color: rgb(var(--danger)) !important; }
.pp-txt-w { color: #b7811c !important; }
.pp-txt-m { color: #94a3b8 !important; }
@media (max-width: 1000px) { .pp-g2 { grid-template-columns: 1fr; } }
@media (max-width: 640px) {
    .pp-g3 { grid-template-columns: 1fr; }
    .pp-cascade { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .pp-semaine { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
</style>
